import { SharedData } from '../canvas/common/typings';
import { BulkReplacementRadio } from '../components/BulkReplacementModeChooser/BulkReplacementModeChooser';
import { KeywordsTextTypeView } from '../components/DisplayConditions/DisplayConditions';
import { ErrorItem } from '../components/ErrorsPresenter/ErrorsPresenter';
import { CanvasSettings } from '../components/ImageCanvasIFrame/ImageCanvasIFrame';
import { SelectItem } from '../components/SelectDecorator/SelectDecorator';
import { TrackingParametersReplacementSelect } from '../components/TrackingParametersBulkReplacement/TrackingParametersBulkReplacement';
import { BannerSummaryErrorParams } from '../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';

import {
    BannerConstants,
    BannerId,
    BannerPrice,
    BannerSiteLinksId,
    Callout,
    CalloutsEditorId,
    CanvasPageType,
    CreativeId,
    GroupPlacementsSummary,
    ImageItem,
    ImageOrCreativeUploadingMode,
    PrevEditedBannerId,
    SiteLinkData,
    TurbolandingPage,
    VcardData,
    VCardsCampaignsDataInfo,
    VcardStatus
} from './banners';
import {
    DropdownChooserItem,
    OperatorModerationInfo,
    PageStatus,
    TurboCreationMode,
    TurboTemplate,
    ValidationErrorData,
    ValidationErrorsData
} from './common';
import {
    AdvType,
    AdvTypeClient,
    BannerImage,
    BannerImageForEdit,
    BannerImageSize,
    BannerPreviewData,
    BannerStatus,
    CampaignId,
    CampaignType,
    ContactsEditorType,
    CopyTooltipStatus,
    GroupType,
    RejectReasonsData
} from './domain';
import {
    CurrencyCode,
    GdAdGroupPrimaryStatus,
    GdBannerMeasurerSystem,
    GdCampaignPrimaryStatus,
    GdCreativeBusinessType,
    GdCreativeStatusModerate,
    GdCreativeType,
    OrderInput
} from './graphql-grid';
import { GroupId } from './groups';
import { EditedBannerTypeInternal } from './internalBannersEditState';
import { OrganizationsEditorState } from './organizations';
import { EditPageRecommendationsData } from './recommendations';
import { TruncatedUserProfile } from './userProfileRetargeting';
import { Omit } from './utils';

export enum TurbolandingType {
    DEFAULT = 'DEFAULT',
    GEO = 'GEO'
}

export interface EditedBannerTurbolanding {
    expanded?: boolean;
    page?: TurbolandingPage;
    trackParams?: string;
    hrefParams?: string | undefined;
    hrefParamsErrors?: ValidationErrorData[];
    loading?: boolean;
    templateId?: TurboTemplate;
    creationMode?: TurboCreationMode;
    isCanvasOpened?: boolean;
    enableTemplates?: string[];
    disableTemplates?: string[];
}

export interface BannersSiteLinksEditorState {
    data: Record<BannerSiteLinksId, BannerSiteLinksEditorData>;
}

export interface EditedBannerSiteLinks {
    isOpen: boolean;

    items: Array<SiteLinkData>;
}

export type SubwayStationForms = Record<string, { genitive: string; locative: string; nominative: string }>;

export interface SubwayStationInfoForPreviewData {
    id: string;
    name: SubwayStationForms;
}

export interface BannerSiteLinksEditorData {
    isDescriptionChecked: boolean;
    isSaving: boolean;
    isCanvasOpened: boolean;
    isUrlChecking: boolean;

    currentSitelink: number;
    turboApiUrl: string;

    items: Array<SiteLinkData>;
    errors: Record<string, ValidationErrorData[]>;
}

export interface EditedBannerImage {
    mode: ImageOrCreativeUploadingMode;
    displayName: string;
    imageUrl: string;
    imageLinkToCrop?: string;
    displayNameToCrop?: string;
    uploadProgress: number | undefined;
    data: BannerImage | BannerImageForEdit | undefined;
    fixedSize?: BannerImageSize;
    uploadingData?: string;
    errors?: ValidationErrorData[];
    isUploadByLink?: boolean;
}

export interface EditedBannerCampaign {
    id: CampaignId;
    name: string;
    platform: string;
    type: CampaignType;
    isOnlyTurboAvailable: boolean;
    hasBanners: boolean;
    status: GdCampaignPrimaryStatus;
}

export interface EditedBannerVCard {
    isShortVersionChecked: boolean;
    isNotShowMetroInSearchChecked: boolean;
    info?: VcardData;
}

export interface EditedBannerVCardDraft {
    isShortVersionChecked: boolean;
    isNotShowMetroInSearchChecked: boolean;
    vCardStatus: VcardStatus;
    info?: VcardData;
}

export type EditedBannerOrganization = Omit<OrganizationsEditorState, 'isVisible'>;

export interface EditedBannerPrice {
    bannerPrice?: BannerPrice;
    errors?: ErrorItem[];
    isOpen: boolean;
    isOnProgress: boolean;
    isHintHidden?: boolean;
}

export interface TurboGalleryDataEditor {
    href: string;
}

export interface EditorBannerPriceData {
    bannerPrice: BannerPrice;
    isVisible: boolean;
    isOnProgress: boolean;
    isPriceFormVisible: boolean;
    errors: ErrorItem[];
}

export type EditorBannerPriceId = BannerId | MassActionId;

export type EditorBannerPriceState = Record<EditorBannerPriceId, EditorBannerPriceData>;

export interface EditedBannerVideo {
    data: BannerVideoData | undefined;
    isCanvasModalOpened?: boolean;
    isCanvasWithCpcVideo?: boolean;
    isOverlay?: boolean;
}

export interface EditedBannerCreative {
    additionalData?: BannerCreativeData[];
    data: BannerCreativeData | undefined;
    isCanvasModalOpened?: boolean;
    pageType?: CanvasPageType;
}

export interface PixelsApiFormat {
    audience: string[];
    audit: string[];
}

export interface BaseBannerContacts {
    turbolanding?: EditedBannerTurbolanding;
    mobileType: ContactsEditorType;
}

export interface EditedBannerFullContacts extends BaseBannerContacts {
    href: string;
    hrefProtocol: ProtocolValue;
    domain: string;
    displayHref: string;
    isUrlChecking: boolean;
    isDomainCleared?: boolean;
    desktopAndTabletType: ContactsEditorType;
}

export interface EditedBannerGroup {
    id: string;
    name: string;
    status: GdAdGroupPrimaryStatus;
    type: GroupType;
    canEdit: boolean;
}

export interface EditBannersSessionValue {
    groupsIds: Array<GroupId>;
    campaignsIds: Array<string>;
    bannersIds: Array<BannerId>;
    isNew: boolean;
}

export interface DisplayConditions {
    isOpen: boolean;
    isLoading: boolean;
    keywords?: string[];
    retargeting?: string[];
    keywordsTextTypeView: KeywordsTextTypeView;
    copyTooltipStatus: CopyTooltipStatus;
    userProfile?: TruncatedUserProfile;
    placements?: GroupPlacementsSummary;
}

export interface EditedBannerBase {
    id: BannerId;
    isNew: boolean;
    isDuplicateTemplate?: boolean;
    markedForDeletion: boolean;
    canBeDeleted: boolean;
    isMobile: boolean;
    isSaved: boolean;
    readOnly: boolean;
    isEditAllow: boolean;

    bannerType: AdvType | AdvTypeClient;
    // флаг именно опциональный
    // undefined - промокода для баннера нет, не проверяем его при сохранении
    // true - промокод у баннера есть, при сохранении проверяем, поменяли ли урл
    // false - прмокод у баннера есть, но он не активен, не проверяем при сохранении
    isPromocodeActive?: boolean;

    campaign: EditedBannerCampaign;
    errors?: ValidationErrorsData;
    status: BannerStatus;
    group: EditedBannerGroup;
}

export type InvalidBannerPromocodes = Record<string, Array<BannerId>>;
export type PromocodesRestrictedDomains = Record<string, Array<string>>;

export interface EditedBannerTypeText extends EditedBannerBase {
    bannerType: AdvType.TEXT | AdvTypeClient.TEXT_MOBILE;
    title: string;
    titleExtension: string;
    body: string;
    vCardId: string;
    permalinkId?: string;
    suggestedPermalinkId?: string;

    contacts: EditedBannerFullContacts;
    imageHash?: string;
    creativeId?: string;

    sitelinksSetId: string | null;
    sitelinks: EditedBannerSiteLinks;

    turboGallery: TurboGalleryDataEditor;

    video?: EditedBannerVideo;
    callouts: EditedBannerCallouts;
    vCard: EditedBannerVCard;

    dynamicDisclaimer?: string;
    linkTail?: string;
    age: string;
    warning: string;
    bannerPriceEditor: EditedBannerPrice;
    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeImage extends EditedBannerBase {
    bannerType: AdvType.IMAGE_AD;

    contacts: EditedBannerFullContacts;
    isImageCanvasOpen: boolean;

    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface PerformanceCreativeEdited {
    id: string;
    url: string;
    // Отсутствует у креативов актуальной версии. Название креатива предыдущей версии.
    name?: string;
    height: number;
    width: number;
    type?: GdCreativeType;
    groupName?: string;
    // У креативов предыдущей версии конструктора groupId === SPECIAL_CREATIVE_GROUP_ID_FOR_GROUP_WITHOUT_ID
    groupId: string;
    // Используется для формирования названия креатива актуальной версии. Отсутствует у креативов предыдущей версии.
    layout?: string;
}

export interface EditedBannerTypePerformance extends EditedBannerBase {
    bannerType: AdvType.PERFORMANCE;
    creative: PerformanceCreativeEdited;
    creativeReplaced: boolean;
    serverCreativeId?: CreativeId;
}

export interface EditedBannerTypeCpcVideo extends EditedBannerBase {
    bannerType: AdvType.CPC_VIDEO;

    contacts: EditedBannerFullContacts;

    video?: EditedBannerVideo;
    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface Measurers {
    measurerSystem: GdBannerMeasurerSystem;
    params: string;
    hasIntegration: boolean;
}

export interface MeasurersForDisplay extends Measurers {
    isSelected: boolean;
    errors?: ValidationErrorData[];
}

export interface EditedBannerTypeCpmBase extends EditedBannerBase {
    isImageCanvasOpen: boolean;
    imageGallery: EditedBannerImageGallery;
    creative?: EditedBannerCreative;
    pixel: string;
    measurers: Measurers[];
    showsCounters: string[];
}

export interface EditedBannerTypeCpmBanner extends EditedBannerTypeCpmBase {
    bannerType: AdvType.CPM_BANNER;
    contacts: EditedBannerFullContacts;
    tnsId?: string;
    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeCpmIndoor extends EditedBannerTypeCpmBase {
    bannerType: AdvType.CPM_INDOOR;
    contacts: EditedBannerFullContacts;
    video?: EditedBannerVideo;
    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeCpmVideo extends EditedBannerTypeCpmBase {
    bannerType: AdvType.CPM_VIDEO;
    contacts: EditedBannerFullContacts;
    tnsId?: string;

    video?: EditedBannerVideo;
    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeCpmGeoproduct extends EditedBannerTypeCpmBase {
    bannerType: AdvType.CPM_GEOPRODUCT;
    contacts: BaseBannerContacts;
    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeCpmOutdoor extends EditedBannerTypeCpmBase {
    bannerType: AdvType.CPM_OUTDOOR;
    contacts: EditedBannerFullContacts;
    video?: EditedBannerVideo;
    creativeId?: string;
    operators: OperatorModerationInfo[];
    rejectReasons?: RejectReasonsData;

    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeCpmAudio extends EditedBannerTypeCpmBase {
    bannerType: AdvType.CPM_AUDIO;

    contacts: EditedBannerFullContacts;
    video?: EditedBannerVideo;
    creativeId?: string;

    displayConditions: DisplayConditions;
}

export interface EditedBannerTypeDynamic extends EditedBannerBase {
    bannerType: AdvType.DYNAMIC;
}

export interface EditedBannerTypeMcBanner extends EditedBannerBase {
    bannerType: AdvType.MCBANNER;
    imageHash?: string;

    contacts: EditedBannerFullContacts;

    displayConditions: DisplayConditions;
}

export type ContentPromotionBannerType = AdvType.CONTENT_PROMOTION_COLLECTION | AdvType.CONTENT_PROMOTION_VIDEO;

export interface EditedBannerTypeContentPromotionBase extends EditedBannerBase {
    bannerType: ContentPromotionBannerType;
    title: string;
    body: string;
    previewUrl: string;
    contentId: string;
    contentPromotionUrl: {
        value: string;
        isChecking: boolean;
    };
    // В продвижении контента используется только ссылка на url и домен.
    // Остальные поля инициализируются дефолтными значениями и далее пока никак не используются.
    contacts: EditedBannerFullContacts;
}

export interface EditedBannerTypeContentPromotionCollection extends EditedBannerTypeContentPromotionBase {
    bannerType: AdvType.CONTENT_PROMOTION_COLLECTION;
}

export interface EditedBannerTypeContentPromotionVideo extends EditedBannerTypeContentPromotionBase {
    bannerType: AdvType.CONTENT_PROMOTION_VIDEO;
}

export type EditedContentPromotionBanner =
    | EditedBannerTypeContentPromotionCollection
    | EditedBannerTypeContentPromotionVideo;

export interface EditedBannerTypeMobileContent extends EditedBannerBase {
    bannerType: AdvType.MOBILE_CONTENT;
}

export type EditedCpmBanner =
    | EditedBannerTypeCpmBanner
    | EditedBannerTypeCpmVideo
    | EditedBannerTypeCpmIndoor
    | EditedBannerTypeCpmOutdoor
    | EditedBannerTypeCpmGeoproduct;

export type EditedBanner =
    | EditedBannerTypeText
    | EditedBannerTypeImage
    | EditedBannerTypePerformance
    | EditedBannerTypeCpcVideo
    | EditedBannerTypeCpmGeoproduct
    | EditedBannerTypeDynamic
    | EditedBannerTypeMcBanner
    | EditedBannerTypeContentPromotionCollection
    | EditedBannerTypeContentPromotionVideo
    | EditedBannerTypeMobileContent
    | EditedBannerTypeInternal
    | EditedBannerTypeCpmBanner
    | EditedBannerTypeCpmVideo
    | EditedBannerTypeCpmIndoor
    | EditedBannerTypeCpmOutdoor
    | EditedBannerTypeCpmAudio;

export function getEditedBannerWithCallouts(banner: EditedBanner): EditedBannerTypeText | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithTurboGallery(banner: EditedBanner): EditedBannerTypeText | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithOperators(banner: EditedBanner): EditedBannerTypeCpmOutdoor | undefined {
    switch (banner.bannerType) {
        case AdvType.CPM_OUTDOOR:
            return banner;
        default:
            return undefined;
    }
}

export type EditedBannersWithFullContacts =
    | EditedBannerTypeText
    | EditedBannerTypeImage
    | EditedBannerTypeCpmBanner
    | EditedBannerTypeCpcVideo
    | EditedBannerTypeCpmVideo
    | EditedBannerTypeCpmOutdoor
    | EditedBannerTypeCpmAudio
    | EditedBannerTypeCpmIndoor
    | EditedBannerTypeMcBanner
    | EditedBannerTypeContentPromotionVideo
    | EditedBannerTypeContentPromotionCollection
    | undefined;

export type EditedBannersWithBaseContacts = EditedBannersWithFullContacts | EditedBannerTypeCpmGeoproduct;

export function getEditedBannerWithFullContacts(banner: EditedBanner): EditedBannersWithFullContacts {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.IMAGE_AD:
        case AdvType.CPM_BANNER:
        case AdvType.CPC_VIDEO:
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_OUTDOOR:
        case AdvType.CPM_INDOOR:
        case AdvType.CPM_AUDIO:
        case AdvType.MCBANNER:
        case AdvType.CONTENT_PROMOTION_VIDEO:
        case AdvType.CONTENT_PROMOTION_COLLECTION:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithBaseContacts(banner: EditedBanner): EditedBannersWithBaseContacts {
    switch (banner.bannerType) {
        case AdvType.CPM_GEOPRODUCT:
            return banner;
        default:
            return getEditedBannerWithFullContacts(banner);
    }
}

export function getTextBannersWithContacts(
    banner: EditedBanner
): EditedBannerTypeText | EditedBannerTypeImage | EditedBannerTypeCpcVideo | EditedBannerTypeMcBanner | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.IMAGE_AD:
        case AdvType.CPC_VIDEO:
        case AdvType.MCBANNER:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithImage(
    banner: EditedBanner
): EditedBannerTypeText | EditedBannerTypeImage | EditedBannerTypeMcBanner | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.IMAGE_AD:
        case AdvType.MCBANNER:
            return banner;
        default:
            return;
    }
}

export function getBannerWithImage(
    banner: EditedBanner
): EditedBannerTypeText | EditedBannerTypeImage | EditedBannerTypeMcBanner | EditedBannerTypeInternal | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.IMAGE_AD:
        case AdvType.MCBANNER:
        case AdvType.INTERNAL:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithVcard(banner: EditedBanner): EditedBannerTypeText | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithOrganization(banner: EditedBanner): EditedBannerTypeText | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export type BannerWithSitelinks = EditedBannerTypeText;

export function getEditedBannerWithSitelinks(banner: EditedBanner): BannerWithSitelinks | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export function bannerHasSitelinks(banner: EditedBanner): banner is BannerWithSitelinks {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

export function getEditedBannerMassSaveActions(banner: EditedBanner): EditedBannerTypeText | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export type BannerWithVideo =
    | EditedBannerTypeText
    | EditedBannerTypeCpcVideo
    | EditedBannerTypeCpmOutdoor
    | EditedBannerTypeCpmIndoor
    | EditedBannerTypeCpmAudio
    | EditedBannerTypeCpmVideo;

export function getEditedBannerWithVideo(banner: EditedBanner): BannerWithVideo | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.CPM_OUTDOOR:
        case AdvType.CPM_INDOOR:
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_AUDIO:
        case AdvType.CPC_VIDEO:
            return banner;
        default:
            return;
    }
}

export function bannerHasVideo(banner: EditedBanner): banner is BannerWithVideo {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.CPM_VIDEO:
        case AdvType.CPC_VIDEO:
            return true;
        default:
            return false;
    }
}

type BannerWithCreativeId = EditedBannerTypeImage | BannerWithVideo | EditedBannerTypeCpmBanner;

export function bannerHasCreativeId(banner: EditedBanner): banner is BannerWithCreativeId {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.CPM_OUTDOOR:
        case AdvType.CPM_INDOOR:
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_AUDIO:
        case AdvType.CPC_VIDEO:
        case AdvType.IMAGE_AD:
        case AdvType.CPM_BANNER:
            return true;

        default:
            return false;
    }
}

type BannerWithImageHash = EditedBannerTypeText | EditedBannerTypeMcBanner;

export function bannerHasImageHash(banner: EditedBanner): banner is BannerWithImageHash {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.MCBANNER:
            return true;

        default:
            return false;
    }
}

export type BannerWithDisplayConditions =
    | EditedBannerTypeText
    | EditedBannerTypeImage
    | EditedBannerTypeMcBanner
    | EditedBannerTypeCpcVideo
    | EditedBannerTypeCpmBanner
    | EditedBannerTypeCpmVideo
    | EditedBannerTypeCpmAudio
    | EditedBannerTypeCpmGeoproduct
    | EditedBannerTypeCpmOutdoor
    | EditedBannerTypeCpmIndoor;

export function bannerHasDisplayConditions(banner: EditedBanner): banner is BannerWithDisplayConditions {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.IMAGE_AD:
        case AdvType.CPC_VIDEO:
        case AdvType.MCBANNER:
        case AdvType.CPM_BANNER:
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_GEOPRODUCT:
        case AdvType.CPM_AUDIO:
        case AdvType.CPM_OUTDOOR:
        case AdvType.CPM_INDOOR:
            return true;
        default:
            return false;
    }
}

export function bannerHasAdditions(banner: EditedBanner | BannerPreviewData): boolean {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.IMAGE_AD:
        case AdvType.CPC_VIDEO:
        case AdvType.CPM_VIDEO:
            return true;
        default:
            return false;
    }
}

export function bannerHasVideoAndImageAdditions(banner: EditedBanner | BannerPreviewData): boolean {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

export const bannerAdvTypesHasCopyPrevEditedBanner = [AdvType.TEXT, AdvType.IMAGE_AD, AdvType.CPC_VIDEO];

export type BannerWithCopyPrevEditedBanner = EditedBannerTypeText | EditedBannerTypeImage | EditedBannerTypeCpcVideo;

export function bannerHasCopyPrevEditedBanner(banner: EditedBanner): banner is BannerWithCopyPrevEditedBanner {
    return [AdvTypeClient.TEXT_MOBILE, ...bannerAdvTypesHasCopyPrevEditedBanner].includes(banner.bannerType);
}

export function getEditedBannerWithCreative(
    banner: EditedBanner
): EditedBannerTypeCpmBanner | EditedBannerTypeCpmGeoproduct | undefined {
    switch (banner.bannerType) {
        case AdvType.CPM_BANNER:
        case AdvType.CPM_GEOPRODUCT:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithPrice(banner: EditedBanner): EditedBannerTypeText | undefined {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return banner;
        default:
            return;
    }
}

export function getEditedBannerWithTnsId(
    banner: EditedBanner
): EditedBannerTypeCpmBanner | EditedBannerTypeCpmVideo | undefined {
    switch (banner.bannerType) {
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_BANNER:
            return banner;
        default:
            return;
    }
}

export function getEditedCpmBanner(
    banner: EditedBanner
):
    | EditedBannerTypeCpmBanner
    | EditedBannerTypeCpmVideo
    | EditedBannerTypeCpmAudio
    | EditedBannerTypeCpmGeoproduct
    | undefined {
    switch (banner.bannerType) {
        case AdvType.CPM_BANNER:
        case AdvType.CPM_AUDIO:
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_GEOPRODUCT:
            return banner;
        default:
            return;
    }
}

export function getEditedContentPromotionBanner(banner: EditedBanner): EditedContentPromotionBanner | undefined {
    switch (banner.bannerType) {
        case AdvType.CONTENT_PROMOTION_VIDEO:
        case AdvType.CONTENT_PROMOTION_COLLECTION:
            return banner;
        default:
            return;
    }
}

type BannerWithTitleAndBody = EditedBannerTypeText | EditedContentPromotionBanner;

export function bannerHasTitleAndBody(banner: EditedBanner): banner is BannerWithTitleAndBody {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
        case AdvType.CONTENT_PROMOTION_COLLECTION:
        case AdvType.CONTENT_PROMOTION_VIDEO:
            return true;
        default:
            return false;
    }
}
type BannerWithTitleExtension = EditedBannerTypeText;

export function bannerHasTitleExtension(banner: EditedBanner): banner is BannerWithTitleExtension {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

type BannerWithAge = EditedBannerTypeText;

export function bannerHasAge(banner: EditedBanner): banner is BannerWithAge {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

type BannerWithLinkTail = EditedBannerTypeText;

export function bannerHasLinkTail(banner: EditedBanner): banner is BannerWithLinkTail {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

type BannerWithDynamicDisclaimer = EditedBannerTypeText;

export function bannerHasDynamicDisclaimer(banner: EditedBanner): banner is BannerWithDynamicDisclaimer {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

type BannerWithWarning = EditedBannerTypeText;

export function bannerHasWarning(banner: EditedBanner): banner is BannerWithWarning {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.TEXT:
            return true;
        default:
            return false;
    }
}

export interface EditedBannerImageGallery {
    images?: ImageItem[];
    isImageGalleryOpen: boolean;
    selectedImageHashFromGallery: Array<string>;
    errors?: Array<ValidationErrorData>;
}

export interface CalloutEditorItem extends Callout {
    search?: {
        textRegex: RegExp;
    };
}

export type CalloutsEditorData = {
    selected: string[];
    text: string;
    isAddingCallouts: boolean;
    isDeletingCallouts: boolean;
    errors?: ValidationErrorData[];
};

export type CalloutsEditorState = {
    data: Record<CalloutsEditorId, CalloutsEditorData>;
    list: Callout[];
};

export type EditedBannerCallouts = {
    selected: string[];
    isEditorOpen: boolean;
};

export interface DraftBanner {
    vCard: EditedBannerVCardDraft;
    errors?: ValidationErrorsData;
}

export interface GroupBanners {
    groupId: string;
    groupName: string;
    bannersIds: BannerId[];
    campaign: EditedBannerCampaign;
    bannersTotal: number;
    status: GdAdGroupPrimaryStatus;
    type: GroupType;
    canEdit: boolean;
    isBannerOnLoad?: boolean;
    autoGeneratedVideo?: EditedBannerVideo;
}

export type BannerClientInfo = {
    clientId: string;
    isBannerUpdateAllowed: boolean;
    isCpmBannerUpdateAllowed: boolean;
    isBannerPricesOnGridEnabled: boolean;
    isTurboForSerpWizardAllowed: boolean;
    isTycoonOrganizationsEnabled: boolean;
    isShowAutoAssignedOrganizationsEnabled: boolean;
    workCurrency: CurrencyCode;
};

export type PerformanceSelectedGroupCreatives = Record<string, boolean>;

export enum CreativeAndGroupsTypeEnum {
    CREATIVE = 'CREATIVE',
    GROUP = 'GROUP'
}

export interface CreativesAndGroupListItem {
    type: CreativeAndGroupsTypeEnum;
    id: string;
}

export type CreativesAndGroupsList = CreativesAndGroupListItem[];

export interface MapPerformanceCreatives {
    creativesAndGroupsList: CreativesAndGroupsList;
    selectedGroupCreatives: PerformanceSelectedGroupCreatives;
    creativeGroups: PerformanceCreativeGroups;
    creativeIds: string[];
    creatives: PerformanceCreatives;
    creativesLoadedCount: number;
}

export type PerformanceCreatives = Record<string, PerformanceCreative>;
export type PerformanceCreativeGroups = Record<string, PerformanceCreativeGroup>;

export interface PerformanceCreativeCampaign {
    id: string;
    name: string;
}

export interface PerformanceCreativeGroup {
    id: string;
    name?: string;
    creativeIds: string[];
}

export interface PerformanceCreative {
    id: string;
    businessType: GdCreativeBusinessType;
    groupId: string;
    groupName?: string;
    name: string;
    height: number;
    width: number;
    url: string;
    statusModerate: GdCreativeStatusModerate;
    layout?: string;
    theme?: string;
    usedInCampaigns: PerformanceCreativeCampaign[];
    regionIds: string[];
}

export enum PerformanceModalType {
    ADDITION = 'ADDITION',
    REPLACEMENT = 'REPLACEMENT'
}

export interface PerformanceModalData {
    groupId: string;
    campaignId: string;
    type: PerformanceModalType;
    bannerId?: string;
}

export interface PerformanceCreativesState {
    isChooseModalOpen: boolean;
    isConstructorOpen: boolean;
    constructorType?: PerformanceCreativesConstructorType;
    constructorCreativeGroupId?: string;
    creativeIds: CreativeId[];
    selectedGroupCreatives: PerformanceSelectedGroupCreatives;
    creatives: PerformanceCreatives;
    creativeGroups: PerformanceCreativeGroups;
    selectedCreatives: Record<CreativeId, boolean>;
    modalData?: PerformanceModalData;
    creativesAndGroupsList: CreativesAndGroupsList;
    creativesLoading: boolean;
    creativesLoadedCount: number;
    searchFieldByIdOrName: string;
    order: OrderInput;
    creativeIdToReplace: CreativeId;
}

export enum PerformanceCreativesConstructorType {
    ADD = 'ADD',
    EDIT = 'EDIT'
}

export interface CanvasModal {
    settings: CanvasSettings;
    sharedData: SharedData;
}

export interface EditBannersState {
    pageStatus: PageStatus;
    banners: Record<BannerId, EditedBanner>;
    bannersServerValues: Record<BannerId, EditedBanner>;
    prevEditedBanners: Record<PrevEditedBannerId, EditedBanner>;
    groupBannersList: Record<string, GroupBanners>;
    groupBannersRecommendations: Record<string, EditPageRecommendationsData>;
    bannersDraft: Record<BannerId, DraftBanner>;
    vCardsIds: Record<CampaignId, string | undefined>;
    bannersIds: Array<BannerId>;
    groupsIds: Array<GroupId>;
    campaignsIds: Array<string>;
    clientInfo: BannerClientInfo;
    clientId?: string;
    constants: BannerConstants;
    isInvalidPromocodesModalOpen: boolean;
    invalidPromocodes: InvalidBannerPromocodes;
    promocodes: PromocodesRestrictedDomains;
    isGeneratedVideoLoading: boolean;
    errorsSummary?: BannerSummaryErrorParams;
    errors: ValidationErrorData[];
    showsCounter?: string;
    performanceCreatives: PerformanceCreativesState;
    isNew: boolean;
    reqId?: string;
    canvasModal: CanvasModal;
    isCopyPrevEditedBannerInProgress: boolean;
    vCardsData: Record<string, VCardsCampaignsDataInfo>;
    defaultOrganizations: Record<CampaignId, string | undefined>;
}

export enum MassActionId {
    bannersEdit = 'bannersEdit',
    bannerGrid = 'bannerGrid'
}

export type BannersEditMassActionsState = Record<MassActionId, BannersEditMassActionState>;

export interface BannersEditMassActionState {
    visibleModalActions: VisibleMassActions;
    data: MassActionsData;
    typeCreative?: AdvType;
    inProgress: boolean;
    errors: ErrorItem[];
}

export interface VisibleMassActions {
    turbolandingBulkReplacementEditorVisible: boolean;
    linkBulkReplacementEditorVisible: boolean;
    linkCreativeBulkReplacementEditorVisible: boolean;
    imageBulkReplacementEditorVisible: boolean;
    textBulkReplacementEditorVisible: boolean;
    linkPathBulkReplacementEditorVisible: boolean;
    linkDisplayedBulkReplacementEditorVisible: boolean;
    imageBulkCreativeReplacementEditorVisible: boolean;
    videoBulkReplacementEditorVisible: boolean;
    cpmImageCreativeBulkReplacementEditorVisible: boolean;
    cpcVideoBulkReplacementEditorVisible: boolean;
    siteLinksEditorVisible: boolean;
    bannerPricesEditorVisible: boolean;
    linkTrackingParametersBulkReplacementEditorVisible: boolean;
    calloutsEditorVisible: boolean;
    pixelEditorVisible: boolean;
    countersEditorVisible: boolean;
}

export interface MassActionsData {
    visibleItems: DropdownChooserItem[];
    targetPartUrl: string;
    newPartUrl: string;
    newVisibleLink: string;
    selectedLink: string;
    newText: string;
    pixel: string;
    counters: string[];
    sourceText: string;
    newParameters: string;
    targetParameters: string;
    keyOrValueReplacement: TrackingParametersReplacementSelect;
    selectedSourceItems: string[];
    selectedProtocolValue: string;
    isTitleTumblerChecked: boolean;
    isSiteLinksTumblerChecked: boolean;
    isAdditionalTextTumblerChecked: boolean;
    currentTextBulkRadioState: BulkReplacementRadio;
    currentSiteLinksActiveRadioButton: BulkReplacementRadio;
    currentCalloutsRadioState: BulkReplacementRadio;
    protocols: Array<SelectItem>;
    replaceOrDeleteToggle: BulkReplacementRadio;
    currentActiveRadioButtonDisplayedLinks: BulkReplacementRadio;
    imageCreative: BannerImageForEdit[];
    currentActiveRadioButtonVideo: BulkReplacementRadio;
    currentActiveRadioButtonCPCVideo: BulkReplacementRadio;
    currentActiveRadioButtonTubolandings: BulkReplacementRadio;
    isDomainAndPathTumblerChecked: boolean;
    isParametersTumblerChecked: boolean;
    creativeAdIds: BannerId[];
}

export interface BannerVideoData {
    id: string;
    name: string;
    previewUrl: string;
    livePreviewUrl?: string;
    formats?: Array<{ width: number; height: number }>;
    duration?: number | string;
}

export interface BannerCreativeData {
    id: string;
    name: string;
    previewUrl: string;
    width: number;
    height: number;
    isAdaptive: boolean;
    isImage: boolean;
    type?: GdCreativeType;
}

export interface BannerVideoCreativeApiData {
    live_preview_url: string;
    name: string;
    stock_creative_id: string;
    resource_url: string;
    additional_data: {
        formats: Array<{ width: number; height: number }>;
        duration: number;
    };
    duration?: string;
}

export interface NewBannerInputData {
    groupId: string;
    campaignId: string;
    bannerType: AdvType | AdvTypeClient;
    bannerData: Partial<Exclude<EditedBanner, 'id'>> & Pick<EditedBanner, 'id'>;
    vCardId?: string;
    video?: EditedBannerVideo;
}

export interface NewPerformanceBannerInputData {
    groupId: string;
    campaignId: string;
    bannerType: AdvType;
    bannerData: Pick<EditedBannerTypePerformance, 'creative'>;
}

export interface GetPerformanceCreativesParams {
    login: string;
    campaignId: string;
    groupId: string;
    limit: number;
    offset: number;
    order: OrderInput;
    idOrName: string;
}

export enum ProtocolValue {
    http = 'http://',
    https = 'https://',
    different = 'http/https'
}

export const protocolMenuItems: SelectItem[] = [
    {
        val: ProtocolValue.https,
        text: ProtocolValue.https
    },
    {
        val: ProtocolValue.http,
        text: ProtocolValue.http
    }
];

export interface BannersIdsGroupsByOpenState {
    siteLinksBannersIds: string[];
    bannerPricesBannersIds: string[];
    bannerPricesEmptyIds: string[];
    calloutsEditorIds: string[];
    siteLinksEmptyBannersIds: string[];
}

export interface BannersIdsGroupsByOpenStateForVisitingCard {
    bannerIdsIsNeedForUpdatingVisitingCard: string[];
    bannerIdsIsEmptyForUpdatingVisitingCard: string[];
}
